import React from 'react';
import { useIntl } from 'react-intl';

import { Grid, Notification } from '@xsolla/ui-core';
import { AlertExclamation } from '@xsolla/ui-icons';

import { usePaths } from '../../../routes/routesHelper';
import { linkTo } from '../../../utils';
import {
  ApiIcon,
  CallbackUrlIcon,
  EmailValidateIcon,
  LinkStylesUnderline,
  PasswordIcon,
  SignUpIcon,
  ToggleBox,
  UsernameSettingsIcon
} from '../../ui';
import NavigationSettingsLink from '../NavigationSettingsLink';
import { ContentWrapper, NotificationWrapper } from './style';

type Props = {
  projectId: string;
};

const ClassicLoginSettings = ({ projectId }: Props): JSX.Element => {
  const {
    apiSettingsPath,
    callbackUrlsSettingsPath,
    changeMethodPath,
    emailSettingsPath,
    passwordSettingsPath,
    signUpSettingsPath,
    usernameSettingsPath
  } = usePaths();
  const intl = useIntl();

  return (
    <ToggleBox
      title={intl.messages['navigation.settings.classic-login.label']}
      openByDefault
      dataTestId="navigation-block-auth">
      <ContentWrapper>
        <NotificationWrapper>
          <Notification icon={AlertExclamation}>
            <LinkStylesUnderline data-test-id="change-project-type">
              {intl.formatMessage(
                { id: 'navigation.settings.classic-login.alert' },
                linkTo({ href: changeMethodPath(projectId) })
              )}
            </LinkStylesUnderline>
          </Notification>
        </NotificationWrapper>
        <Grid cols={2} gap={24}>
          <NavigationSettingsLink
            dataTestId="link-navigation-callback-urls"
            icon={<CallbackUrlIcon />}
            titleInfo={{
              label: intl.messages['navigation.settings.callback.title'],
              path: callbackUrlsSettingsPath(projectId)
            }}
            subtitle={intl.messages['navigation.settings.callback.subtitle']}
          />
          <NavigationSettingsLink
            dataTestId="link-navigation-username-settings"
            icon={<UsernameSettingsIcon />}
            titleInfo={{
              label: intl.messages['navigation.settings.username.title'],
              path: usernameSettingsPath(projectId)
            }}
            subtitle={intl.messages['navigation.settings.username.subtitle']}
          />
          <NavigationSettingsLink
            dataTestId="link-navigation-password-settings"
            icon={<PasswordIcon />}
            titleInfo={{
              label: intl.messages['navigation.settings.classic-login.password.title'],
              path: passwordSettingsPath(projectId)
            }}
            subtitle={intl.messages['navigation.settings.classic-login.password.subtitle']}
          />
          <NavigationSettingsLink
            dataTestId="link-navigation-email-validation"
            icon={<EmailValidateIcon />}
            titleInfo={{
              label: intl.messages['navigation.settings.email-validation.title'],
              path: emailSettingsPath(projectId)
            }}
            subtitle={intl.messages['navigation.settings.email-validation.subtitle']}
          />
          <NavigationSettingsLink
            dataTestId="link-navigation-sign-up-settings"
            icon={<SignUpIcon />}
            titleInfo={{
              label: intl.messages['navigation.settings.sign-up.title'],
              path: signUpSettingsPath(projectId)
            }}
            subtitle={intl.messages['navigation.settings.sign-up.subtitle']}
          />
          <NavigationSettingsLink
            dataTestId="link-navigation-api-settings"
            icon={<ApiIcon />}
            titleInfo={{
              label: intl.messages['navigation.settings.authentication.api-settings.title'],
              path: apiSettingsPath(projectId)
            }}
            subtitle={intl.messages['navigation.settings.authentication.api-settings.subtitle']}
          />
        </Grid>
      </ContentWrapper>
    </ToggleBox>
  );
};

export default ClassicLoginSettings;
